import {Edge, Node} from 'reactflow';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';

import {
  EntityMap_entityMap_EntityMapNode_entities as EntityMapEntityNodeOrInaccessibleInfoNode,
  EntityMap_entityMap_EntityMapNode_edges_EntityMapEdgeNode as EntityMapEdgeNode,
  EntityMap_entityMap_EntityMapNode_connectedSchemas_EntityMapConnectedSchemaNode as EntityMapConnectedSchemaNode,
} from './__generated/EntityMap';

/**
 * Graph nodes keyed by entity id, edges keyed by edge id, a list of fully expanded entity ids, and a list of
 * entity ids that were not expanded due to the product limit.
 */
export interface EntityMapGraph {
  graphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>;
  graphEdgesById: Map<string, Edge>;
  connectedSchemas: Array<EntityMapConnectedSchemaNode>;
}

/**
 * Parses entity map entities and edges into react flow nodes and edges to return an EntityMapGraph.
 */
export function parseEntityMapIntoEntityMapGraph({
  entities,
  edges,
  connectedSchemas,
}: {
  entities: Array<EntityMapEntityNodeOrInaccessibleInfoNode>;
  edges: Array<EntityMapEdgeNode>;
  connectedSchemas: Array<EntityMapConnectedSchemaNode>;
}): EntityMapGraph {
  const graphNodesById = new Map(
    entities.map((entity) => entityMapEntityNodeToReactFlowNode(entity)).map((node) => [node.id, node])
  );
  const graphEdgesById = new Map(edges.map((edge) => [edge.id, entityMapEdgeNodeToReactFlowEdge(edge)]));
  return {
    graphNodesById,
    graphEdgesById,
    connectedSchemas,
  };
}

/**
 * Converts an EntityMapEntityNode to a ReactFlow Node
 *
 * @param entity The EntityMapEntityNode to convert
 * @returns The ReactFlow Node
 */
export function entityMapEntityNodeToReactFlowNode(
  entity: EntityMapEntityNodeOrInaccessibleInfoNode
): Node<EntityMapEntityNodeOrInaccessibleInfoNode> {
  return {
    id: isInaccessible(entity) ? entity.invalidId : entity.id,
    type: 'entity',
    data: entity,
    position: {x: 0, y: 0},
  };
}

/**
 * Converts an EntityMapEdgeNode to a ReactFlow Edge
 *
 * @param edge The EntityMapEdgeNode to convert
 * @returns The ReactFlow Edge
 */
export function entityMapEdgeNodeToReactFlowEdge(edge: EntityMapEdgeNode): Edge {
  return {
    id: edge.id,
    source: edge.sourceId,
    target: edge.targetId,
    type: 'entityEdge',
    // TODO(BNCH-126927): Provide a label for the edge
    ...getTestProps({component: 'EntityMapEdge', key: edge.id}),
  };
}

/**
 * Merges two graph nodes by id, preserving the positions of existing nodes.
 */
export function mergeGraphNodesById(
  oldGraphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>,
  newGraphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>
): Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>> {
  const graphNodesById = new Map(oldGraphNodesById.entries());

  newGraphNodesById.forEach((graphNode, entityId) => {
    if (graphNodesById.has(entityId)) {
      graphNodesById.set(entityId, {
        ...graphNodesById.get(entityId)!,
        data: graphNode.data,
      });
    } else {
      graphNodesById.set(entityId, graphNode);
    }
  });

  return graphNodesById;
}

function extractEdgeCountsFromReactFlowNodes(
  graphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>
): Map<string, number> {
  return new Map(
    Array.from(graphNodesById.entries()).map(([entityId, graphNode]) => [
      entityId,
      isInaccessible(graphNode.data) ? 0 : graphNode.data.edgeCount,
    ])
  );
}

/**
 * Counts how many edges each node participates in within the current graph.
 */
function extractCurrentGraphEdgeCountsById(graphEdgesById: Map<string, Edge>): Map<string, number> {
  const edgeCountsById = new Map<string, number>();

  for (const edge of graphEdgesById.values()) {
    const sourceCount = edgeCountsById.get(edge.source) || 0;
    const targetCount = edgeCountsById.get(edge.target) || 0;

    edgeCountsById.set(edge.source, sourceCount + 1);
    edgeCountsById.set(edge.target, targetCount + 1);
  }

  return edgeCountsById;
}

/**
 * Extracts fully expanded entity ids from a graph nodes map by comparing the node's total edge count against
 * how many edges it participates in within the current graph.
 */
export function extractFullyExpandedEntityIds({
  graphNodesById,
  graphEdgesById,
}: Pick<EntityMapGraph, 'graphNodesById' | 'graphEdgesById'>): Set<string> {
  const graphNodesCountsById = extractEdgeCountsFromReactFlowNodes(graphNodesById);
  const currentGraphEdgeCountsById = extractCurrentGraphEdgeCountsById(graphEdgesById);

  const fullyExpandedEntityIds = new Set(
    Array.from(graphNodesById.entries())
      .filter(([entityId, graphNode]) => {
        if (isInaccessible(graphNode.data)) {
          return false;
        }
        const totalEdgeCount: number = graphNodesCountsById.get(entityId)!;
        const currentEdgeCount: number = currentGraphEdgeCountsById.get(entityId) || 0;
        // Fuzzy matching is used here to handle scenarios where after loading edges on the frontend, an edge
        // is removed on the backend. In that case, the loaded edge count will always be greater than the
        // current edge count, as extra edges on the frontend will not be removed.
        return totalEdgeCount <= currentEdgeCount;
      })
      .map(([entityId, _]) => entityId)
  );

  return fullyExpandedEntityIds;
}
